const statusLabels = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
  IN_PROCESS: "Em processo",
  PLANNED: "Planejado",
};

const dateBr = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value || "");
};

const yearsFrom = (value) => {
  const birthday = value ? new Date(`${String(value).slice(0, 10)}T12:00:00`) : null;
  if (!birthday || Number.isNaN(birthday.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - birthday.getFullYear();
  const beforeBirthday = now.getMonth() < birthday.getMonth()
    || (now.getMonth() === birthday.getMonth() && now.getDate() < birthday.getDate());
  if (beforeBirthday) years -= 1;
  return years >= 0 ? String(years) : "";
};

const currency = (cents) => {
  const amount = Number(cents || 0) / 100;
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const yesNo = (value) => value === true ? "Sim" : value === false ? "Não" : "";

const slug = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "");

const hasDocumentValue = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDocumentValue = (...values) =>
  values.find((value) => hasDocumentValue(value));

const documentValueAt = (source, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => (current == null ? undefined : current[key]), source);

const documentValue = (source, ...paths) =>
  firstDocumentValue(...paths.map((path) => documentValueAt(source, path)));

const deepDocumentValue = (source, aliases = []) => {
  const wanted = new Set((aliases || []).map((alias) => slug(alias)).filter(Boolean));
  if (!wanted.size || !source || typeof source !== "object") return undefined;

  const queue = [{ value: source, depth: 0 }];
  const seen = new Set();
  while (queue.length) {
    const { value, depth } = queue.shift();
    if (!value || typeof value !== "object" || seen.has(value) || depth > 7) continue;
    seen.add(value);

    for (const [key, child] of Object.entries(value)) {
      const normalizedKey = slug(key);
      if (
        wanted.has(normalizedKey) &&
        hasDocumentValue(child) &&
        (typeof child !== "object" || child === null)
      ) {
        return child;
      }
      if (
        child &&
        typeof child === "object" &&
        !Array.isArray(child) &&
        !["dataUrl", "photoUrl", "files", "file"].includes(key)
      ) {
        queue.push({ value: child, depth: depth + 1 });
      }
    }
  }
  return undefined;
};

const documentValueAny = (source, paths = [], aliases = paths) =>
  firstDocumentValue(
    documentValue(source, ...paths),
    deepDocumentValue(source, aliases),
  );

const normalizeYoungApprenticeDocumentData = (rawEmployment) => {
  const root = rawEmployment && typeof rawEmployment === "object" ? rawEmployment : {};
  const employmentSource =
    root.employment && typeof root.employment === "object" ? root.employment : root;
  const companySource =
    employmentSource.company && typeof employmentSource.company === "object"
      ? employmentSource.company
      : root.company && typeof root.company === "object"
        ? root.company
        : employmentSource.companyData && typeof employmentSource.companyData === "object"
          ? employmentSource.companyData
          : {};

  const company = {
    ...companySource,
    corporateName: documentValueAny(
      companySource,
      ["corporateName", "legalName", "razaoSocial", "companyName", "name"],
      ["corporateName", "legalName", "razaoSocial", "companyName", "name", "emp_razao", "emp_razao_social", "empresa"],
    ),
    tradeName: documentValueAny(
      companySource,
      ["tradeName", "fantasyName", "nomeFantasia"],
      ["tradeName", "fantasyName", "nomeFantasia", "emp_fantasia", "emp_nome_fantasia"],
    ),
    cnpj: documentValueAny(
      companySource,
      ["cnpj", "taxId"],
      ["cnpj", "taxId", "emp_cnpj"],
    ),
    stateRegistration: documentValueAny(
      companySource,
      ["stateRegistration", "inscricaoEstadual"],
      ["stateRegistration", "inscricaoEstadual", "emp_ie", "emp_inscricao_estadual"],
    ),
    street: documentValueAny(
      companySource,
      ["street", "address.street", "address.addressLine1", "logradouro", "rua"],
      ["street", "addressLine1", "logradouro", "rua", "endereco", "emp_endereco", "empresa_endereco"],
    ),
    number: documentValueAny(
      companySource,
      ["number", "address.number", "numero"],
      ["number", "numero", "numeroEndereco", "emp_numero", "empresa_numero"],
    ),
    complement: documentValueAny(
      companySource,
      ["complement", "address.complement", "complemento"],
      ["complement", "complemento", "emp_complemento", "empresa_complemento"],
    ),
    neighborhood: documentValueAny(
      companySource,
      ["neighborhood", "address.neighborhood", "bairro", "district"],
      ["neighborhood", "bairro", "district", "emp_bairro", "empresa_bairro"],
    ),
    city: documentValueAny(
      companySource,
      ["city", "address.city", "cidade"],
      ["city", "cidade", "municipio", "emp_cidade", "empresa_cidade"],
    ),
    state: documentValueAny(
      companySource,
      ["state", "address.state", "uf"],
      ["state", "uf", "estado", "emp_uf", "emp_estado", "empresa_uf"],
    ),
    zipCode: documentValueAny(
      companySource,
      ["zipCode", "address.zipCode", "postalCode", "cep"],
      ["zipCode", "postalCode", "cep", "emp_cep", "empresa_cep"],
    ),
    phone1: documentValueAny(companySource, ["phone1", "phone"], ["phone1", "phone", "telefone", "emp_telefone"]),
    phone2: documentValueAny(companySource, ["phone2"], ["phone2", "telefone2", "emp_telefone2"]),
    whatsapp: documentValueAny(companySource, ["whatsapp"], ["whatsapp", "emp_whatsapp"]),
    phones: documentValue(companySource, "phones"),
    whatsapps: documentValue(companySource, "whatsapps"),
    emails: documentValue(companySource, "emails"),
    notes: documentValueAny(companySource, ["notes", "observacoes"], ["notes", "observacoes", "emp_observacoes"]),
    complementaryInfo: documentValueAny(
      companySource,
      ["complementaryInfo", "informacoesComplementares"],
      ["complementaryInfo", "informacoesComplementares", "emp_informacoes_complementares"],
    ),
    status: documentValue(companySource, "status"),
  };

  const employment = {
    ...employmentSource,
    company,
    admissionDate: documentValueAny(
      employmentSource,
      ["admissionDate", "hireDate", "dataAdmissao"],
      ["admissionDate", "hireDate", "dataAdmissao", "leg_admissao", "leg_dtadmissao"],
    ),
    startDate: documentValueAny(
      employmentSource,
      ["startDate", "contractStartDate", "dataInicio", "inicioEmpresa"],
      ["startDate", "contractStartDate", "dataInicio", "inicioEmpresa", "leg_inicio", "leg_dtinicio", "data_inicio_contrato"],
    ),
    endDate: documentValueAny(
      employmentSource,
      [
        "endDate",
        "contractEndDate",
        "terminationDate",
        "benefits.terminationDate",
        "benefits.endDate",
        "benefits.contractEndDate",
        "sourceMetadata.conere.terminationDate",
        "sourceMetadata.conere.endDate",
        "dataFim",
        "fimContrato",
      ],
      [
        "endDate",
        "contractEndDate",
        "terminationDate",
        "dataFim",
        "fimContrato",
        "dataEncerramento",
        "dataTermino",
        "dtFim",
        "dtTermino",
        "leg_dtfim",
        "leg_dttermino",
        "leg_datafim",
        "leg_data_termino",
      ],
    ),
    role: documentValueAny(employmentSource, ["role", "position", "jobTitle", "funcao"], ["role", "position", "jobTitle", "funcao", "leg_funcao"]),
    workload: documentValueAny(employmentSource, ["workload", "cargaHoraria"], ["workload", "cargaHoraria", "leg_carga_horaria"]),
    hoursPerDay: documentValueAny(employmentSource, ["hoursPerDay", "horasPorDia"], ["hoursPerDay", "horasPorDia", "leg_horas_dia"]),
    salaryCents: documentValue(employmentSource, "salaryCents", "salarioCentavos"),
    scholarshipCents: documentValue(
      employmentSource,
      "scholarshipCents",
      "bolsaCentavos",
    ),
    courseName: documentValue(
      employmentSource,
      "courseName",
      "course.name",
      "cursoAprendizagem",
    ),
    courseDay: documentValueAny(employmentSource, ["courseDay", "diaCurso"], ["courseDay", "diaCurso", "dia_curso"]),
    courseTime: documentValueAny(employmentSource, ["courseTime", "horarioCurso"], ["courseTime", "horarioCurso", "horario_curso"]),
    terminationReason: documentValueAny(
      employmentSource,
      ["terminationReason", "motivoEncerramento", "benefits.terminationReason"],
      ["terminationReason", "motivoEncerramento", "motivoTermino", "leg_motivo_encerramento"],
    ),
  };

  return { employment, company };
};

const ignoredObjectKeys = new Set([
  "id",
  "dataUrl",
  "photoUrl",
  "url",
  "fileUrl",
  "relativePath",
  "downloadPath",
  "downloadUrl",
]);

const fieldLabels = {
  name: "Nome",
  relationship: "Parentesco",
  cpf: "CPF",
  whatsapp: "WhatsApp",
  phone: "Telefone",
  phone1: "Telefone 1",
  phone2: "Telefone 2",
  educationLevel: "Escolaridade",
  incomeCents: "Renda",
  livesWithStudent: "Reside com o aluno",
  institution: "Instituição",
  value: "Valor",
  status: "Status",
  notes: "Observações",
};

const valueForKey = (key, value) => {
  if (key === "incomeCents") return value === undefined || value === null || value === "" ? "" : currency(value);
  if (key === "livesWithStudent") return yesNo(value);
  if (/date$/i.test(key)) return dateBr(value);
  if (key === "status") return statusLabels[value] || value;
  return printable(value);
};

function printable(value) {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "boolean") return yesNo(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => printable(item))
      .filter(Boolean)
      .join("\n");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([key, child]) => !ignoredObjectKeys.has(key) && child !== undefined && child !== null && child !== "")
      .map(([key, child]) => {
        const rendered = valueForKey(key, child);
        return rendered ? `${fieldLabels[key] || key}: ${rendered}` : "";
      })
      .filter(Boolean);
    if (entries.length) return entries.join(" — ");
    if (value.name || value.label || value.title) return String(value.name || value.label || value.title);
    return "";
  }
  return String(value);
}

const personSummary = (person = {}) => printable({
  name: person.name,
  relationship: person.relationship,
  cpf: person.cpf,
  whatsapp: person.whatsapp,
  phone1: person.phone1,
  phone2: person.phone2,
  educationLevel: person.educationLevel,
  incomeCents: person.incomeCents,
  livesWithStudent: person.livesWithStudent,
});

const addressSummary = (address = {}) => {
  const first = [address.street, address.number].filter(Boolean).join(", ");
  return [first, address.complement, address.neighborhood, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(" — ");
};

const formatCnpj = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length !== 14) return String(value || "");
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const formatCpf = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 11
    ? digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")
    : String(value || "");
};

const companyAddressSummary = (company = {}) => {
  const first = [company.street, company.number].filter(Boolean).join(", ");
  return [
    first,
    company.complement,
    company.neighborhood,
    company.city,
    company.state,
    company.zipCode,
  ]
    .filter(Boolean)
    .join(" — ");
};

const companyContactSummary = (company = {}) => {
  const phones = Array.from(
    new Set([
      ...(Array.isArray(company.phones) ? company.phones : []),
      company.phone1,
      company.phone2,
    ].filter(Boolean)),
  );
  const whatsapps = Array.from(
    new Set([
      ...(Array.isArray(company.whatsapps) ? company.whatsapps : []),
      company.whatsapp,
    ].filter(Boolean)),
  );
  const emails = Array.from(
    new Set((Array.isArray(company.emails) ? company.emails : []).filter(Boolean)),
  );
  return [
    phones.length ? `Telefone: ${phones.join(" / ")}` : "",
    whatsapps.length ? `WhatsApp: ${whatsapps.join(" / ")}` : "",
    emails.length ? `E-mail: ${emails.join(" / ")}` : "",
  ]
    .filter(Boolean)
    .join(" — ");
};

const moneyOrBlank = (value) =>
  value === undefined || value === null || value === "" ? "" : currency(value);

const longDate = () => new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "long",
  year: "numeric",
}).format(new Date());

const addVariable = (state, id, label, value, group = "Aluno") => {
  const normalizedId = slug(id);
  if (!normalizedId || state.ids.has(normalizedId)) return;
  state.ids.add(normalizedId);
  state.fields.push({ id: normalizedId, label, type: "text", group });
  const rendered = printable(value);
  state.values[normalizedId] = rendered;

  // Também mantém o rótulo humano como alias técnico oculto. Isso permite que
  // modelos antigos/importados usem {{Rua da empresa}}, {{Data de encerramento}}
  // etc. sem depender exatamente do id interno escolhido pelo editor.
  const labelAlias = slug(label);
  if (
    labelAlias &&
    !Object.prototype.hasOwnProperty.call(state.values, labelAlias)
  ) {
    state.values[labelAlias] = rendered;
  }
};

const flattenHiddenValues = (state, value, path = []) => {
  if (value === undefined || value === null || value === "") return;
  if (Array.isArray(value)) {
    const id = slug(path.join("_"));
    if (id) state.values[id] = printable(value);
    return;
  }
  if (typeof value !== "object") {
    const id = slug(path.join("_"));
    if (id) state.values[id] = printable(value);
    return;
  }
  Object.entries(value).forEach(([key, child]) => {
    if (["dataUrl", "photoUrl", "files"].includes(key)) return;
    flattenHiddenValues(state, child, [...path, key]);
  });
};

export const buildStudentDocumentVariables = ({
  student = {},
  classes = [],
  courses = [],
  attendance = [],
  youngApprenticeEnabled = false,
  youngApprenticeEmployment = null,
  customStudentTabs = [],
} = {}) => {
  const state = { fields: [], values: {}, ids: new Set() };
  const klass = classes.find((item) => Number(item.id) === Number(student.classId));
  const course = courses.find((item) => Number(item.id) === Number(klass?.courseId));
  const family = student.family || {};
  const responsible1 = family.responsible1 || {};
  const responsible2 = family.responsible2 || {};
  const members = Array.isArray(family.members) ? family.members : [];
  const residents = [responsible1, responsible2, ...members].filter((person) => person?.livesWithStudent);
  const incomeCents = residents.reduce((sum, person) => sum + Number(person?.incomeCents || 0), 0);
  const attendanceEntries = attendance.flatMap((session) => (session.entries || [])
    .filter((entry) => Number(entry.studentId) === Number(student.id))
    .map((entry) => ({ ...entry, session })));
  const present = attendanceEntries.filter((entry) => entry.status === "PRESENT").length;
  const absent = attendanceEntries.filter((entry) => entry.status === "ABSENT").length;
  const justified = attendanceEntries.filter((entry) => entry.status === "JUSTIFIED").length;

  const common = [
    ["id_aluno", "ID do aluno", student.id],
    ["numero_matricula", "Número de matrícula", student.registrationNumber],
    ["ra", "RA", student.ra],
    ["nome_completo", "Nome completo", student.fullName],
    ["nome_social", "Nome social", student.socialName],
    ["data_inscricao", "Data de inscrição", dateBr(student.enrollmentDate)],
    ["data_nascimento", "Data de nascimento", dateBr(student.birthDate)],
    ["idade", "Idade", yearsFrom(student.birthDate)],
    ["genero", "Gênero", student.gender],
    ["etnia", "Etnia/cor", student.ethnicity],
    ["nacionalidade", "Nacionalidade", student.nationality],
    ["nis", "NIS", student.nis],
    ["cpf", "CPF", student.cpf],
    ["rg", "RG", student.rg],
    ["whatsapp", "WhatsApp", student.whatsapp],
    ["telefone", "Telefone", student.phone],
    ["email", "E-mail", student.email],
    ["status", "Status", statusLabels[student.status] || student.status],
    ["data_saida", "Data de saída", dateBr(student.exitDate)],
    ["motivo_saida", "Motivo de saída", student.exitReason],
    ["turma", "Turma", klass?.name],
    ["periodo_turma", "Período da turma", klass?.period],
    ["curso", "Curso", course?.name],
    ["coordenador_curso", "Coordenador do curso", course?.coordinator],
    ["data_vinculo_turma", "Data de vínculo com a turma", dateBr(student.classLinkedAt)],
    ["data_atual", "Data atual", new Date().toLocaleDateString("pt-BR")],
    ["data_atual_extenso", "Data atual por extenso", longDate()],
  ];
  common.forEach(([id, label, value]) => addVariable(state, id, label, value, "Dados do aluno"));
  // Aliases legados permanecem disponíveis para modelos já salvos, sem aparecer
  // duplicados na lista lateral de variáveis.
  state.values.matricula = state.values.numero_matricula;
  state.values.nome_aluno = state.values.nome_completo;
  state.values.data_matricula = state.values.data_inscricao;

  const address = student.address || {};
  [
    ["cep", "CEP", address.zipCode], ["rua", "Rua", address.street],
    ["numero", "Número", address.number], ["complemento", "Complemento", address.complement],
    ["bairro", "Bairro", address.neighborhood], ["cidade", "Cidade", address.city],
    ["estado", "Estado", address.state], ["zona", "Zona", address.zone],
    ["tipo_moradia", "Tipo de moradia", address.housingType],
    ["endereco_completo", "Endereço completo", addressSummary(address)],
  ].forEach(([id, label, value]) => addVariable(state, id, label, value, "Endereço"));

  const addResponsible = (prefix, labelPrefix, person) => {
    [
      [`${prefix}_nome`, `${labelPrefix} — Nome`, person.name],
      [`${prefix}_cpf`, `${labelPrefix} — CPF`, person.cpf],
      [`${prefix}_whatsapp`, `${labelPrefix} — WhatsApp`, person.whatsapp],
      [`${prefix}_parentesco`, `${labelPrefix} — Parentesco`, person.relationship],
      [`${prefix}_escolaridade`, `${labelPrefix} — Escolaridade`, person.educationLevel],
      [`${prefix}_renda`, `${labelPrefix} — Renda`, person.incomeCents === undefined ? "" : currency(person.incomeCents)],
      [`${prefix}_reside_com_aluno`, `${labelPrefix} — Reside com o aluno`, yesNo(person.livesWithStudent)],
    ].forEach(([id, label, value]) => addVariable(state, id, label, value, "Família e responsáveis"));
  };
  addResponsible("responsavel_1", "Responsável 1", responsible1);
  addResponsible("responsavel_2", "Responsável 2", responsible2);
  addVariable(state, "responsaveis", "Responsáveis", [responsible1, responsible2].map(personSummary).filter(Boolean), "Família e responsáveis");
  addVariable(state, "integrantes_familiares", "Integrantes familiares", members.map(personSummary), "Família e responsáveis");
  addVariable(state, "quantidade_residentes", "Quantidade de residentes", residents.length, "Família e responsáveis");
  addVariable(state, "renda_familiar_total", "Renda familiar total", currency(incomeCents), "Família e responsáveis");
  addVariable(state, "renda_per_capita", "Renda per capita", residents.length ? currency(Math.round(incomeCents / residents.length)) : "", "Família e responsáveis");

  const authorizations = student.authorizations?.pickupPersons || [];
  addVariable(
    state,
    "pessoas_autorizadas",
    "Pessoas autorizadas a buscar",
    authorizations.map(personSummary),
    "Autorizações",
  );

  const social = student.social || {};
  addVariable(state, "cras", "CRAS de referência", social.cras, "Social");
  addVariable(state, "renda_familiar_social", "Renda familiar informada no social", social.familyIncome, "Social");
  addVariable(state, "beneficios_sociais", "Benefícios sociais", social.benefits, "Social");

  const education = student.education || {};
  addVariable(state, "escolaridade", "Escolaridade", education.level, "Escolaridade");
  addVariable(state, "escola", "Escola", education.school, "Escolaridade");
  addVariable(state, "serie_ano", "Série/Ano", education.grade, "Escolaridade");
  addVariable(state, "periodo_escolar", "Período escolar", education.period, "Escolaridade");
  addVariable(state, "cursos_extras", "Cursos extras", education.extraCourses, "Escolaridade");

  addVariable(state, "total_aulas", "Total de aulas registradas", attendanceEntries.length, "Frequência");
  addVariable(state, "total_presencas", "Total de presenças", present, "Frequência");
  addVariable(state, "total_faltas", "Total de faltas", absent, "Frequência");
  addVariable(state, "total_justificadas", "Total de faltas justificadas", justified, "Frequência");
  addVariable(state, "percentual_presenca", "Percentual de presença", attendanceEntries.length ? `${Math.round((present / attendanceEntries.length) * 100)}%` : "", "Frequência");

  if (youngApprenticeEnabled) {
    const { employment, company } =
      normalizeYoungApprenticeDocumentData(youngApprenticeEmployment);
    const companyName = company.tradeName || company.corporateName || "";
    const phones = Array.from(
      new Set([
        ...(Array.isArray(company.phones) ? company.phones : []),
        company.phone1,
        company.phone2,
      ].filter(Boolean)),
    );
    const whatsapps = Array.from(
      new Set([
        ...(Array.isArray(company.whatsapps) ? company.whatsapps : []),
        company.whatsapp,
      ].filter(Boolean)),
    );
    const emails = Array.from(
      new Set((Array.isArray(company.emails) ? company.emails : []).filter(Boolean)),
    );
    const employmentStatus = {
      ACTIVE: "Ativo",
      PLANNED: "Planejado",
      ENDED: "Encerrado",
    }[employment.status] || employment.status;

    [
      ["nome_empresa", "Nome da empresa", companyName],
      ["nome_fantasia_empresa", "Nome fantasia da empresa", company.tradeName],
      ["razao_social_empresa", "Razão social da empresa", company.corporateName],
      ["cnpj_empresa", "CNPJ da empresa", formatCnpj(company.cnpj)],
      ["responsavel_legal_empresa", "Responsável legal da empresa", company.legalRepresentativeName],
      ["cpf_responsavel_legal_empresa", "CPF do responsável legal da empresa", formatCpf(company.legalRepresentativeCpf)],
      ["inscricao_estadual_empresa", "Inscrição estadual da empresa", company.stateRegistration],
      ["endereco_empresa", "Endereço completo da empresa", companyAddressSummary(company)],
      ["rua_empresa", "Rua da empresa", company.street],
      ["numero_empresa", "Número da empresa", company.number],
      ["complemento_empresa", "Complemento da empresa", company.complement],
      ["bairro_empresa", "Bairro da empresa", company.neighborhood],
      ["cidade_empresa", "Cidade da empresa", company.city],
      ["estado_empresa", "Estado da empresa", company.state],
      ["cep_empresa", "CEP da empresa", company.zipCode],
      ["contato_empresa", "Contato da empresa", companyContactSummary(company)],
      ["telefone_empresa", "Telefone da empresa", phones.join(" / ")],
      ["whatsapp_empresa", "WhatsApp da empresa", whatsapps.join(" / ")],
      ["email_empresa", "E-mail da empresa", emails.join(" / ")],
      ["observacoes_empresa", "Observações da empresa", company.notes],
      ["informacoes_complementares_empresa", "Informações complementares da empresa", company.complementaryInfo],
      ["status_empresa", "Status da empresa", company.status === "ACTIVE" ? "Ativa" : company.status === "INACTIVE" ? "Inativa" : company.status],
      ["status_vinculo", "Status do vínculo", employmentStatus],
      ["funcao", "Função", employment.role],
      ["carga_horaria", "Carga horária", employment.workload],
      ["horas_por_dia", "Horas por dia", employment.hoursPerDay],
      ["salario", "Salário", moneyOrBlank(employment.salaryCents)],
      ["bolsa", "Bolsa", moneyOrBlank(employment.scholarshipCents)],
      ["curso_aprendizagem", "Curso de aprendizagem", employment.courseName],
      ["dia_curso", "Dia de curso", employment.courseDay],
      ["horario_curso", "Horário do curso", employment.courseTime],
      ["admissao", "Data de admissão", dateBr(employment.admissionDate)],
      ["inicio_empresa", "Início na empresa", dateBr(employment.startDate)],
      ["encerramento", "Data de encerramento", dateBr(employment.endDate)],
      ["motivo_encerramento", "Motivo do encerramento", employment.terminationReason],
      ["vale_transporte", "Vale-transporte", yesNo(employment.transportationVoucher)],
      ["desconto_vale_transporte", "Descontar vale-transporte", yesNo(employment.deductTransportationVoucher)],
      ["vale_alimentacao", "Vale-alimentação", yesNo(employment.mealAllowance)],
      ["valor_vale_alimentacao", "Valor do vale-alimentação", moneyOrBlank(employment.mealAllowanceCents)],
      ["banco_vinculo", "Banco do vínculo", employment.bankName],
      ["agencia_vinculo", "Agência bancária", employment.bankAgency],
      ["conta_vinculo", "Conta bancária", employment.bankAccount],
      ["inicio_ferias", "Início das férias", dateBr(employment.vacationStartDate)],
      ["dias_ferias", "Dias de férias", employment.vacationDays],
      ["ferias_cobradas", "Férias cobradas", yesNo(employment.vacationBilled)],
      ["observacoes_vinculo", "Observações do vínculo", employment.notes],
    ].forEach(([id, label, value]) =>
      addVariable(state, id, label, value, "Mundo do Trabalho"),
    );
    const aliasVariable = (alias, sourceId) => {
      const aliasId = slug(alias);
      const sourceKey = slug(sourceId);
      if (
        !aliasId ||
        Object.prototype.hasOwnProperty.call(state.values, aliasId)
      ) return;
      state.values[aliasId] = state.values[sourceKey] || "";
    };

    [
      ["empresa", "nome_empresa"],
      ["local_de_trabalho", "nome_empresa"],
      ["local_trabalho", "nome_empresa"],
      ["nome_da_empresa", "nome_empresa"],
      ["nome_fantasia", "nome_fantasia_empresa"],
      ["razao_social", "razao_social_empresa"],
      ["rua_da_empresa", "rua_empresa"],
      ["logradouro_empresa", "rua_empresa"],
      ["endereco_da_empresa", "endereco_empresa"],
      ["endereco_completo_empresa", "endereco_empresa"],
      ["numero_da_empresa", "numero_empresa"],
      ["complemento_da_empresa", "complemento_empresa"],
      ["bairro_da_empresa", "bairro_empresa"],
      ["cidade_da_empresa", "cidade_empresa"],
      ["estado_da_empresa", "estado_empresa"],
      ["uf_empresa", "estado_empresa"],
      ["cep_da_empresa", "cep_empresa"],
      ["data_admissao", "admissao"],
      ["data_inicio_contrato", "inicio_empresa"],
      ["inicio_atividades_praticas", "inicio_empresa"],
      ["data_inicio_atividades_praticas", "inicio_empresa"],
      ["fim_contrato", "encerramento"],
      ["fim_do_contrato", "encerramento"],
      ["data_fim_contrato", "encerramento"],
      ["data_do_fim_de_contrato", "encerramento"],
      ["data_do_fim_do_contrato", "encerramento"],
      ["data_de_encerramento", "encerramento"],
      ["data_encerramento", "encerramento"],
      ["curso_jovem_aprendiz", "curso_aprendizagem"],
    ].forEach(([alias, sourceId]) => aliasVariable(alias, sourceId));
  }

  const customFields = student.customFields || {};
  const isConereTab = (title) => slug(title).includes("conere");
  const configuredTabs = Array.isArray(customStudentTabs) ? customStudentTabs : [];
  const configuredTitles = new Set(configuredTabs.map((tab) => String(tab?.title || "")));

  configuredTabs.forEach((tab) => {
    const title = String(tab?.title || "").trim();
    if (!title || isConereTab(title)) return;
    const values =
      customFields[title] && typeof customFields[title] === "object"
        ? customFields[title]
        : {};
    (Array.isArray(tab?.fields) ? tab.fields : []).forEach((field) => {
      const label = String(field?.label || "").trim();
      if (!label || isConereTab(label)) return;
      addVariable(
        state,
        `personalizado_${title}_${label}`,
        label,
        values[label],
        title,
      );
    });
  });

  Object.entries(customFields).forEach(([tab, values]) => {
    if (isConereTab(tab) || configuredTitles.has(tab)) return;
    Object.entries(values && typeof values === "object" ? values : {}).forEach(([field, value]) => {
      if (isConereTab(field)) return;
      addVariable(state, `personalizado_${tab}_${field}`, field, value, tab);
    });
  });

  // Mantém tokens técnicos antigos funcionando em modelos já salvos, mas eles
  // não entram em "fields" e portanto não aparecem mais na lista do editor.
  flattenHiddenValues(state, student, ["aluno"]);

  // Registro interno não é uma aba personalizada. Mantém seus tokens antigos
  // para modelos salvos, sem expor os campos técnicos no seletor/IA.
  return { fields: state.fields.filter(field => field.group !== "worldOfWorkRecord"), values: state.values };
};

export const groupStudentDocumentVariables = (fields = []) => fields.reduce((groups, field) => {
  const group = field.group || "Aluno";
  groups[group] = groups[group] || [];
  groups[group].push(field);
  return groups;
}, {});
